import { getDemandwareUrl } from '~/helpers/utils'
import { resolveLocalDevSfccOrigin } from '~/utils/local-dev-sfcc'

import type { Facet, FacetOption } from '@constructor-io/constructorio-client-javascript'
import { PLP_FILTERS } from '../config/plpFilters'

// REVIEW
type Context = {
  env: {
    mhjEnvironment: string | undefined
  }
  i18n: {
    locale: string
    localeCodes?: string[]
    t?: Function
  }
}

// TODO: Refactor into constants URL config
const myAccountBaseURL = '/my-account'

const addLocalePrefixToPath = (locale, originalPath?) => {
  const path = originalPath ?? ''
  return localeConfig?.[locale]?.pathPrefix ? `/${localeConfig?.[locale]?.pathPrefix}${path}` : path
}

export function getSiteId(locale: Locales): string {
  const siteId = localeConfig[locale]?.siteId
  return siteId || 'MichaelHillAU'
}

const CountryDomainMaps = {
  AU: 'michaelhill.com.au',
  CA: 'michaelhill.ca',
  NZ: 'michaelhill.co.nz',
}

interface WishlistUrl {
  authenticated: string
  guest: string
}

function getDomain(context: Context, isDemandware: Boolean = false) {
  if (import.meta.dev && !isDemandware) {
    // Enhance DX for local development. Eg. /my-bag, /checkout/shipping
    return ''
  }
  const { mhjEnvironment } = context.env
  if (/^(dev\d|ods)\d$/.test(mhjEnvironment)) {
    // (Developer) ODS boxes
    return getDemandwareUrl(mhjEnvironment)
  }
  const { countryCode } = getCountryCodeAndLocale(context)
  const prefix = mhjEnvironment === 'prod' ? 'www' : mhjEnvironment
  const suffix = CountryDomainMaps[countryCode] ?? '<invalid-locale>'
  return `https://${prefix}.${suffix}`
}

export function getBaseSiteUrl(context: Context) {
  const {
    i18n: { locale },
  } = context
  const domain = getDomain(context)
  const localePrefix = locale.split('-')[0]
  const suffix = localePrefix !== 'en' ? `${localePrefix}/` : ''
  return `${domain}/${suffix}`
}

export function getEnvironment(origin: string) {
  try {
    const url = new URL(origin)
    const subdomain = url.hostname.split('.')[0]
    switch (subdomain) {
      case 'www':
      case 'mhill-prod-nextgen':
      case 'michael-hill-prod':
        return 'prod'
      case 'mhill-stage-nextgen':
      case 'michael-hill-stage':
        return 'stage'
      case 'michael-hill-dev':
      case 'mhill-dev-nextgen':
        return 'dev'
      case 'michael-hill-dev2':
        return 'dev2'
      default:
        return subdomain
    }
  } catch (error) {
    console.error('Invalid URL:', origin, error)
    return
  }
}

function getCountryCodeAndLocale(context: Context) {
  const {
    i18n: { locale },
  } = context
  const countryCode = locale === 'default' ? 'AU' : locale.slice(locale.indexOf('-') + 1)
  const localeStr = locale === 'en-AU' ? 'default' : locale.replace('-', '_')
  return { countryCode, localeStr }
}

function getLocalePath(context: Context, path: string, isRelativePath?: boolean) {
  const localePath = addLocalePrefixToPath(context.i18n.locale, path)
  return isRelativePath ? localePath : `${getDomain(context)}${localePath}`
}

function getWishlistUrl(context: Context): WishlistUrl {
  const {
    i18n: { locale },
  } = context
  return {
    authenticated: addLocalePrefixToPath(locale, '/my-account/wishlist'),
    guest: addLocalePrefixToPath(locale, '/wishlist'),
  }
}

const reduceFilters =
  (query: Record<string, string | string[] | null | undefined>) =>
  (prev: Record<string, string[]>, curr: string): Record<string, string[]> => {
    const val = query[curr]

    let value: string[] = []
    if (val !== undefined && val !== null) {
      if (Array.isArray(val)) {
        value = val.map((v) => decodeURIComponentSafe(String(v)))
      } else {
        value = [decodeURIComponentSafe(String(val))]
      }
    }

    return {
      ...prev,
      [curr]: value,
    } as Record<string, string[]>
  }

const decodeURIComponentSafe = (str: string): string => {
  try {
    return decodeURIComponent(str)
  } catch {
    return str
  }
}

const mapFilterToGroupedFacet = (filter: Facet) => ({
  id: filter.attributeId,
  label: filter.label,
  options: filter.values.map(mapFilterValueToAgnosticFacet.bind(null, filter)),
})

const mapFilterValueToAgnosticFacet = (filter: Facet, option: FacetOption) => ({
  type: 'attribute',
  attrName: filter.attributeId,
  id: option.value,
  value: option.value,
  count: option.hitCount,
  selected: option.selected,
  label: option.label,
})

export const useSiteUrl = () => {
  /* Recreating a VSF Context Object */
  const route = useRoute()
  const router = useRouter()
  const i18n = useNuxtApp().$i18n
  const locale = i18n.locale.value
  const localeCodes = Object.values(localeConfig).map((locale) => locale.iso)
  const config = useRuntimeConfig()
  const requestOrigin = resolveRequestOrigin()
  const mhjEnvironment = getEnvironment(
    resolveLocalDevSfccOrigin(requestOrigin, config.public.SFCC_ORGANIZATION_ID, {
      origin: config.public.SFCC_ORIGIN,
    }) || requestOrigin,
  )
  const context: Context = { i18n: { locale, localeCodes, t: i18n.t }, env: { mhjEnvironment } }

  const baseSiteUrl = getBaseSiteUrl(context)

  const urlToCategoryId = (url: string): string => {
    return url.replace(/\//g, '_')
  }

  const categoryIdToUrl = (url: string): string => {
    return url.replace(/_/g, '/')
  }

  const localizeURL = (url: string) => {
    const prefix = localeConfig[locale]?.pathPrefix
    /* Eg: In fr locale, if a relative url doesn't start with /fr/ - append /fr  */
    const localeUrl = prefix && url?.startsWith('/') && !url?.startsWith(`/${prefix}/`) ? `/${prefix}${url}` : url
    return localeUrl
  }

  const getFiltersDataFromUrl = (
    onlyFilters: boolean,
    handleQueryConversion: boolean = false,
  ): Record<string, string[]> => {
    const { query } = route
    const filtersDataFromUrl = Object.keys(query)
      .filter((f) => (onlyFilters ? PLP_FILTERS.includes(f) : !PLP_FILTERS.includes(f)))
      .reduce(reduceFilters(query), {})
    const prefn1 = queryString(query.prefn1)
    if (handleQueryConversion && prefn1 && query.prefv1) {
      filtersDataFromUrl[`c_${prefn1}`] = queryStrings(query.prefv1).map((value) => decodeURIComponent(value))
    }
    return filtersDataFromUrl
  }

  const getFacetsFromURL = () => {
    const { query, params } = route
    const categorySlug = Object.keys(params).reduce((prev, curr) => params[curr] || prev, params.slug_1)
    return {
      categorySlug,
      page: parseInt(query.page as string, 10) || 1,
      sort: query.sort as string,
      sortByValue: query.sortBy as string,
      sortOrderValue: query.sortOrder as string,
      filters: getFiltersDataFromUrl(true, true),
      itemsPerPage: parseInt(query.itemsPerPage as string, 10) || 20,
      q: Array.isArray(query.q) ? query.q[0] : query.q,
    }
  }

  const changeFilters = (filters: Record<string, string[]>, windowScrollY: number): void => {
    router.push({
      query: {
        ...getFiltersDataFromUrl(false),
        ...filters,
      },
      params: { windowScrollY: `${windowScrollY}` },
    })
  }

  const getFormattedFilters = (filters: Record<string, string[]>) => {
    return Object.keys(filters).reduce<Record<string, string[]>>((acc, key) => {
      const newKey = key.replace('c_', '')
      acc[newKey] = filters[key]
      return acc
    }, {})
  }

  const getGrouped = (searchData: any) => {
    if (!searchData?.data?.facets) {
      return []
    }

    return searchData.data.facets.map(mapFilterToGroupedFacet)
  }

  return {
    accountUrl: `${addLocalePrefixToPath(locale)}${myAccountBaseURL}`,
    bagUrl: getLocalePath(context, '/my-bag', true),
    baseSiteUrl,
    mhjEnvironment,
    storesUrl: getLocalePath(context, '/stores', true),
    wishlist: getWishlistUrl(context),
    getLocalePrefix: () => addLocalePrefixToPath(locale),
    urlToCategoryId,
    categoryIdToUrl,
    localizeURL,
    getFacetsFromURL,
    changeFilters,
    getFormattedFilters,
    getGrouped,
  }
}
